import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import i18n from 'simple-react-i18n'
import { push } from '@lagunovsky/redux-react-router'
import { chunk, compact, countBy, flatten, fromPairs, isNil, keyBy, maxBy, some, sortBy, uniq } from 'lodash'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import CityAction from 'referencial/components/city/actions/CityAction'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import ProgressCard from 'components/card/ProgressCard'
import DashboardAddStationDialog from 'station/components/dashboard/component/DashboardAddStationDialog'
import DashboardMapPanel from 'station/components/dashboard/component/DashboardMapPanel'
import DashboardStationsPanel from 'station/components/dashboard/component/DashboardStationsPanel'
import { MAP, STATION_LIST, STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from 'station/constants/StationConstants'
import HomeAction from 'home/actions/HomeAction'
import AdministrationAction from 'administration/actions/AdministrationAction'
import { getLabel } from 'utils/StoreUtils'
import { getUserBookmarksByStationType } from 'utils/UserUtil'
import { findStationType, getBookmarks } from 'utils/StationUtils'
import { getSandreLabel, getSandreMnemonique, normalizeString } from 'utils/StringUtil'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import { hasValue } from 'utils/NumberUtil'
import DistributionUnitAction from 'distributionUnit/actions/DistributionUnitAction'
import { componentHasHabilitations } from 'utils/HabilitationUtil'
import { H_DISTRIBUTION_DASHBOARD } from 'account/constants/AccessRulesConstants'
import UserAction from 'administration/components/user/actions/UserAction'
import StationAction from 'station/actions/StationAction'
import ContributorAction from 'referencial/components/contributor/actions/ContributorAction'
import QualityAction from 'quality/actions/QualityAction'
import ReferencialAction from 'referencial/action/ReferencialAction'
import ManagementUnitAction from 'referencial/components/managementUnit/actions/ManagementUnitAction'
import useApplicationSetting, { intParser } from 'utils/customHook/useApplicationSetting'
import { DEFAULT_CONTRIBUTOR_TYPE, FILTER_TYPE } from 'administration/components/user/constants/UserConstants'
import SimpleTabList from 'components/list/SimpleTabList'
import DistributionUnitFilterPanel from './DistributionUnitFilterPanel'
import moment from 'moment'
import { Chip, Stack } from '@mui/material'
import DistributionUnitIndicators from './DistributionUnitIndicators'
import { CONFORMITY_COLORS, getConformityFilterBody } from 'quality/utils/conformityExportUtils'
import useAbortController from 'utils/customHook/useAbortController'
import ToastrAction from 'toastr/actions/ToastrAction'
import LogAction from 'log/actions/LogAction'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import { DEFAULT_SUPPORT, SEUILS_LIM, SEUILS_REF } from 'quality/components/AEPoverview/AEPOverview'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import { COMPLIANT, getComplianceStatus, LIMIT_EXCEEDED, NO_RECENT_OPERATION, REFERENCE_EXCEEDED } from 'station/constants/ComplianceConstants'
import PropTypes from 'prop-types'
import { disabledBorderInputColor, green, INDICATORS_COLORS, whiteColor } from 'utils/constants/ColorTheme'
import { CONFORM, NO_DATA, NO_INDICATOR, NON_CONFORM, REFERENCE_EXCEEDEDED } from 'distributionUnit/utils/DistributionUnitUtils'
import useSandreList from 'utils/customHook/useSandreList'
import useListIndexed from 'utils/customHook/useListIndexed'
import { getWGS84Coordinate } from 'utils/mapUtils/CoordinateUtils'
import PiezometryAction from 'piezometry/actions/PiezometryAction'
import HydrometryAction from 'hydrometry/actions/HydrometryAction'
import PluviometryAction from 'pluviometry/actions/PluviometryAction'
import useBoolean from 'utils/customHook/useBoolean'
import { getUnitDistribMarker2 } from 'utils/SvgUtil'

const SEARCH_VALUE_HEADERS = ['number', 'code', 'name', 'city', 'status', 'SISEeaucode', 'creationDate', 'stationTypeLabel', 'operator', 'administrator', 'referent', 'department']
const DISTRIBUTION_UNITS_HEADERS = [{ key: 'bookmarkIcon', value: '' }, { key: 'conformityChip', value: '' }, 'code', 'name', 'sector', 'status', 'waterNature', 'exploitationModeCode', 'subscriberNumber', 'productionQuantity', 'distributedQuantity']
const DISTRIBUTION_UNITS_EXPORT_HEADERS = ['bookmark', 'conformity', 'code', 'name', 'sector', 'status', 'waterNature', 'exploitationModeCode', 'subscriberNumber', 'productionQuantity', 'distributedQuantity']
const STATION_TYPE = STATION_TYPE_NAME.distributionUnit

const STORAGE_SEUILS_REF = 'DEFAULT_REFERENCE_THRESHOLD'
const STORAGE_SEUILS_LIM = 'DEFAULT_LIMIT_THRESHOLD'

const MIN_GROUP_SIZE = 5
const MAX_GROUP_SIZE = 20
const TARGET_GROUPS = 10
const getGroupSize = nbUnits => Math.min(MAX_GROUP_SIZE, Math.max(MIN_GROUP_SIZE, Math.ceil(nbUnits / TARGET_GROUPS)))

const PROGRESS_REFRESH_MS = 200
const DEFAULT_MS_PER_UNIT = 300
const MAX_IN_FLIGHT_RATIO = 0.95

const linkComplianceValues = (compliance = '') => {
    switch (compliance) {
        case LIMIT_EXCEEDED: return NON_CONFORM
        case REFERENCE_EXCEEDED: return REFERENCE_EXCEEDEDED
        case COMPLIANT: return CONFORM
        case NO_RECENT_OPERATION: return NO_DATA
        default: return NO_INDICATOR
    }
}

const ColourfulChip = ({
    title,
    color = disabledBorderInputColor,
}) => (
    <Chip
        label={title}
        size={'small'}
        sx={{ mr: 1, maxHeight: '16px', minHeight: '16px', backgroundColor: color, color: whiteColor,
        }}
    />
)

ColourfulChip.propTypes = {
    title: PropTypes.string,
    color: PropTypes.string,
}

const DistributionUnitsMapLoader = ({
    stations = [],
}) => {
    const dispatch = useDispatch()

    const {
        cities,
        piezometers,
        qualitometers,
        hydrometers,
        pluviometers,
        distributionUnitAssociations,
    } = useSelector(store => ({
        cities: store.CityReducer.cities,
        piezometers: store.PiezometryReducer.piezometersLight,
        qualitometers: store.QualityReducer.qualitometersLight,
        hydrometers: store.HydrometryReducer.hydrometricStations,
        pluviometers: store.PluviometryReducer.pluviometers,
        distributionUnitAssociations: store.DistributionUnitReducer.distributionUnitAssociations,
    }), shallowEqual)

    const citiesIndex = useListIndexed(cities, 'id')

    const {
        progress,
        isLoaded,
    } = useProgressDispatch(() => compact([
        !qualitometers.length && dispatch(QualityAction.fetchQualitometersLight()),
        !piezometers.length && dispatch(PiezometryAction.fetchPiezometersLight()),
        !hydrometers.length && dispatch(HydrometryAction.fetchHydrometricStations()),
        !pluviometers.length && dispatch(PluviometryAction.fetchPluviometers()),
        !distributionUnitAssociations.length && dispatch(DistributionUnitAction.fetchAllDistributionUnitsAssociations()),
    ]))

    const stationCoordsByKey = useMemo(() => [ ...piezometers, ...hydrometers, ...pluviometers, ...qualitometers].reduce((acc, act) => {
        acc[`${act.typeName}_${act.id}`] = act
        return acc
    }, {},
    ), [piezometers, hydrometers, pluviometers, qualitometers])

    const distributionUnitsWithStations = useMemo(() => (
        isLoaded ? stations?.map(udi => {
            const associatedStations = distributionUnitAssociations.filter(l => l.id === udi.id)
            return {
                id: udi.id,
                code: udi.code,
                name: udi.name,
                compliance: udi.compliance ?? NO_INDICATOR,
                stations: associatedStations
                    .map((assoc) => {
                        const station = stationCoordsByKey[`${assoc.typeName}_${assoc.stationLinkedId}`]
                        return {
                            id: assoc.stationLinkedId,
                            code: assoc.stationLinkedCode,
                            name: assoc.stationLinkedName,
                            stationType: assoc.stationLinkedType,
                            typeName: assoc.typeName,
                            countType: assoc.countType,
                            x: station?.x,
                            y: station?.y,
                            projection: 31,
                            townCode: station?.townCode,
                        }
                    }),
            }
        }) : []
    ), [stations, isLoaded, distributionUnitAssociations, stationCoordsByKey])

    const getComplianceColor = (compliance) => {
        switch (compliance) {
            case LIMIT_EXCEEDED: return CONFORMITY_COLORS.nonConform
            case REFERENCE_EXCEEDED: return CONFORMITY_COLORS.referenceExceeded
            case COMPLIANT: return CONFORMITY_COLORS.conform
            case NO_RECENT_OPERATION: return INDICATORS_COLORS.GREY
            case NO_DATA: return INDICATORS_COLORS.BLACK
            default: return green
        }
    }

    const getCoordinates = useCallback((point) => {
        if (!isNil(point.x) && !isNil(point.y)) {
            return getWGS84Coordinate(point)
        }
        const city = citiesIndex[point.townCode]
        if (city && !isNil(city.x) && !isNil(city.y)) {
            return [city.x, city.y]
        }
        return null
    }, [citiesIndex])

    const distributionUnitPoints = useMemo(() => (
        distributionUnitsWithStations
            .filter((du) => du.stations.length)
            .map((du) => {
                const validStations = du.stations
                    .map(s => {
                        if (s.x > 180 || s.x < -180 || s.y > 90 || s.y < -90) {
                            const coordinates = getCoordinates({
                                x: s.x,
                                y: s.y,
                                projection: 31,
                                townCode: s.townCode,
                            })

                            return {
                                ...s,
                                x: coordinates[0],
                                y: coordinates[1],
                                city: citiesIndex[s.townCode],
                            }
                        }

                        return { ...s, city: citiesIndex[s.townCode] }
                    })
                    .filter(s => Number.isFinite(s.x) && Number.isFinite(s.y))

                const x = validStations.reduce((sum, s) => sum + s.x, 0) / (validStations.length || 1)
                const y = validStations.reduce((sum, s) => sum + s.y, 0) / (validStations.length || 1)
                const nbCities = compact(uniq(validStations.map(s => s.city))).length
                const stationTypeCounts = countBy(du.stations, 'typeName')

                return {
                    id: du.id,
                    code: du.code,
                    name: du.name,
                    x,
                    y,
                    projection: du.stations[0]?.projection,
                    nbCities,
                    stationTypeCounts,
                    stationType: STATION_TYPE_NAME.distributionUnit,
                    typeName: STATION_TYPE_NAME.distributionUnit,
                    markerIcon: getUnitDistribMarker2(getComplianceColor(du.compliance)),
                    colorLine: getComplianceColor(du.compliance) === green ? whiteColor : getComplianceColor(du.compliance),
                    compliance: du.compliance,
                }
            })
    ), [distributionUnitsWithStations, getCoordinates, citiesIndex])

    return isLoaded && (<DashboardMapPanel
        stationType={STATION_TYPE}
        data={{ title: i18n.distributionUnits, stations: distributionUnitPoints }}
    />) || (<ProgressCard progress={progress}/>)
}

DistributionUnitsMapLoader.propTypes = {
    stations: PropTypes.arrayOf(PropTypes.shape({})),
}

const DistributionUnitsPanel = ({
}) => {
    const dispatch = useDispatch()

    const {
        accountUser,
        userBookmarks,
        contributorLinks,
        contributors,
        sandreCodes,
        distributionUnits,
        distributionUnitSectors,
        selectedSearchValues,
        globalResearch,
    } = useSelector(store => ({
        accountUser: store.AccountReducer.accountUser,
        userBookmarks: store.UserReducer.userBookmarks,
        contributorLinks: store.StationReducer.contributorLinks,
        contributors: store.ContributorReducer.contributors,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        distributionUnits: store.DistributionUnitReducer.distributionUnits,
        distributionUnitSectors: store.DistributionUnitReducer.distributionUnitSectors,
        selectedSearchValues: store.AdministrationReducer.selectedSearchValues,
        globalResearch: store.HomeReducer.globalResearch,
    }), shallowEqual)

    const defaultSupport = useAccountSetting(DEFAULT_SUPPORT, setting => setting || undefined)
    const thresholdRefSettings = useApplicationSetting(SEUILS_REF, setting => setting ? parseInt(setting) : undefined)
    const thresholdRefAccountSettings = useAccountSetting(SEUILS_REF, setting => setting ? parseInt(setting) : undefined)
    const thresholdRefDefaultSettings = useLocalStorage(STORAGE_SEUILS_REF, setting => setting ? parseInt(setting) : undefined)[0]

    const thresholdLimSettings = useApplicationSetting(SEUILS_LIM, setting => setting ? parseInt(setting) : undefined)
    const thresholdLimAccountSettings = useAccountSetting(SEUILS_LIM, setting => setting ? parseInt(setting) : undefined)
    const thresholdLimDefaultSettings = useLocalStorage(STORAGE_SEUILS_LIM, setting => setting ? parseInt(setting) : undefined)[0]

    const contributorsList = useSandreList(SANDRE.CONTRIBUTOR_TYPES)
    const operatorType = useApplicationSetting('contributorTypeOperator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.OPERATOR
    const referentType = useApplicationSetting('contributorTypeAdministrator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.ADMINISTRATOR

    const distributionDynamicHeaders = [
        ...DISTRIBUTION_UNITS_HEADERS.slice(0, DISTRIBUTION_UNITS_HEADERS.length / 2),
        contributorsList.find(c => c.code === referentType)?.name,
        contributorsList.find(c => c.code === operatorType)?.name,
        ...DISTRIBUTION_UNITS_HEADERS.slice(DISTRIBUTION_UNITS_HEADERS.length / 2),
    ]
    const distributionDynamicExportHeaders = [
        ...DISTRIBUTION_UNITS_EXPORT_HEADERS.slice(0, DISTRIBUTION_UNITS_EXPORT_HEADERS.length / 2),
        contributorsList.find(c => c.code === referentType)?.name,
        contributorsList.find(c => c.code === operatorType)?.name,
        ...DISTRIBUTION_UNITS_EXPORT_HEADERS.slice(DISTRIBUTION_UNITS_EXPORT_HEADERS.length / 2),
    ]

    const {
        value: isOpened,
        setTrue: open,
        setFalse: close,
    } = useBoolean(false)
    const {
        value: autoSearchDone,
        setTrue: setAutoSearchDone,
    } = useBoolean(false)
    const {
        value: isComplianceFetched,
        setTrue: complianceIsFetched,
    } = useBoolean(false)

    const [filters, setFilters] = useState({
        searchValue: globalResearch || selectedSearchValues[STATION_TYPE]?.searchValue || '',
        filter: FILTER_TYPE.NOFILTER,
        filterResults: selectedSearchValues[STATION_TYPE]?.filterResults || [],
        sector: undefined,
        city: undefined,
        referentIds: [],
        operatorIds: [],
        indicators: [NO_INDICATOR, CONFORM, REFERENCE_EXCEEDEDED, NON_CONFORM, NO_DATA],
    })
    const [results, setResults] = useState({})
    const [displayedProgress, setDisplayedProgress] = useState(0)
    const { controllerRef, initController } = useAbortController(true)

    const {
        isLoaded: areProducersLoaded,
    } = useProgressDispatch(() => [
        dispatch(ContributorAction.fetchProducers()),
    ])

    useEffect(() => {
        if (globalResearch) {
            dispatch(HomeAction.updateGlobalResearch(''))
        }
        dispatch(StationAction.fetchAllContributors(STATION_TYPE_CONSTANT.distributionUnit))
    }, [])

    const onValidateAll = (object) => {
        setFilters(object)
        dispatch(AdministrationAction.setSelectedSearchValues(STATION_TYPE, { searchValue: object.searchValue }))
    }

    const getContributor = useCallback((udi, ref) => {
        const now = moment().valueOf()
        const availableContributorLinks = contributorLinks.filter(cl => udi.id === cl.idStation && cl.contributorType === ref && (!cl.endDate || cl.endDate > now)).map(c => ({ ...c, startDate: c.startDate ?? 0 }))
        const contributorLink = availableContributorLinks.length ? maxBy(availableContributorLinks, 'startDate') : undefined
        const contributor = contributorLink && contributors.find(c => contributorLink.idContributor === c.id)
        return contributor && (contributor.mnemonique || contributor.name) || ''
    }, [contributorLinks, contributors])

    const operator = useApplicationSetting('contributorTypeOperator', d => parseInt(d)) || DEFAULT_CONTRIBUTOR_TYPE.OPERATOR
    const administrator = useApplicationSetting('contributorTypeAdministrator', d => parseInt(d)) || DEFAULT_CONTRIBUTOR_TYPE.ADMINISTRATOR

    const distributionUnitsFormatted = useMemo(() => {
        if (distributionUnits.length) {
            return sortBy(distributionUnits, o => o.name ? o.name.toUpperCase() : '}')
                .map(udi => ({
                    ...udi,
                    sector: getLabel(distributionUnitSectors, udi.id, 'name', 'distributionUnitId'),
                    sectorObj: distributionUnitSectors.find(s => s.distributionUnitId === udi.id),
                    bookmarkIcon: getBookmarks(udi.code, getUserBookmarksByStationType(userBookmarks, 'distributionUnit', udi.code)),
                    bookmark: getBookmarks(udi.code, getUserBookmarksByStationType(userBookmarks, 'distributionUnit', udi.code)) ? i18n.yes : i18n.no,
                    waterNature: getSandreLabel(sandreCodes, SANDRE.NATURE_EAU, udi.waterNature),
                    [contributorsList.find(c => c.code === operatorType)?.name]: getContributor(udi, operator),
                    operator: getContributor(udi, operator),
                    [contributorsList.find(c => c.code === referentType)?.name]: getContributor(udi, administrator),
                    collectivity: getContributor(udi, administrator),
                    exploitationModeCode: getSandreMnemonique(sandreCodes, SANDRE.MODE_EXPLOITATION, udi.exploitationModeCode),
                    status: getSandreLabel(sandreCodes, SANDRE.CODE_ETAT, udi.state),
                }))
        }
        return []
    }, [distributionUnits, distributionUnitSectors, userBookmarks, sandreCodes, getContributor, operator, administrator, operatorType, referentType, contributorsList])

    const containsSearchValue = useCallback((station) =>
        some(SEARCH_VALUE_HEADERS, prop => station[prop]
            ? normalizeString(station[prop].toString()).includes(normalizeString(filters.searchValue))
            : false,
        ), [filters.searchValue])

    const data = useMemo(() => {
        const {
            searchValue,
            filter,
            filterResults,
            sector,
            city,
            referentIds,
            operatorIds,
        } = filters

        const now = moment().valueOf()

        const udiBySector = (sector && sector !== FILTER_TYPE.NOFILTER) ? distributionUnitsFormatted.filter(udi => udi.sectorObj?.code === sector) : distributionUnitsFormatted
        const distributionUnitsFiltered = city ? udiBySector.filter(udi => udi.geographicSector?.split(',')?.includes(city)) : udiBySector

        const valuableContributors = contributorLinks.filter(l => l.endDate >= now || !l.endDate)
        const valuableOperators = valuableContributors.filter(l => l.contributorType === operatorType && operatorIds?.includes(l.idContributor))
        const valuableReferents = valuableContributors.filter(l => l.contributorType === referentType && referentIds?.includes(l.idContributor))

        const operatorFiltered = !operatorIds?.length ?
            distributionUnitsFiltered :
            distributionUnitsFiltered.filter(s => valuableOperators.some(l => l.idStation === s.id))
        const referentFiltered = !referentIds?.length ?
            operatorFiltered :
            operatorFiltered.filter(s => valuableReferents.some(l => l.idStation === s.id))

        const searchFiltered = searchValue ? referentFiltered.filter(s => containsSearchValue(s)) : referentFiltered

        if (!hasValue(filter) || `${filter}` === `${FILTER_TYPE.NOFILTER}`) {
            return searchFiltered
        } else if (`${filter}` === `${FILTER_TYPE.BOOKMARK}`) {
            const bookmarks = getUserBookmarksByStationType(userBookmarks, STATION_TYPE)
            return flatten(bookmarks.map(code => searchFiltered.find(s => s.code.includes(code)) || []))
        }
        if (filterResults.length) {
            return flatten(filterResults.map(fr => searchFiltered.find(station => station.id === parseInt(fr.id)) || []))
        }
        return []
    }, [containsSearchValue, userBookmarks, filters, operatorType, referentType, contributorLinks, distributionUnitsFormatted])

    const unitsIds = distributionUnitsFormatted.map(({ id }) => id)

    useEffect(() => {
        if (autoSearchDone || !areProducersLoaded || !unitsIds.length) {
            return
        }
        const conformityFilter = {
            support: defaultSupport,
            referenceThreshold: thresholdRefDefaultSettings ?? thresholdRefAccountSettings ?? thresholdRefSettings,
            limitThreshold: thresholdLimDefaultSettings ?? thresholdLimAccountSettings ?? thresholdLimSettings,
        }
        if (!hasValue(conformityFilter.referenceThreshold) && !hasValue(conformityFilter.limitThreshold)) {
            setDisplayedProgress(-1)
            return
        }
        setAutoSearchDone()
        controllerRef.current.abort()
        initController()
        const { signal } = controllerRef.current
        const body = {
            ...getConformityFilterBody(conformityFilter),
            startDate: moment().subtract(1, 'year').valueOf(),
            endDate: moment().valueOf(),
        }
        const groups = chunk(unitsIds, getGroupSize(unitsIds.length))
        setResults({})

        const computeNext = async (groupNumber = 0, failedOnce = false) => {
            if (signal.aborted || groupNumber >= groups.length) {
                return
            }
            const ids = groups[groupNumber]
            const groupResults = await DistributionUnitAction.promiseConformity(ids, body, signal).catch(err => {
                if (!signal.aborted) {
                    dispatch(LogAction.logError(`${i18n.fetchError}${i18n.AEPoverview} : ${err}`))
                }
                return undefined
            })
            if (signal.aborted) {
                return
            }
            if (!Array.isArray(groupResults) && !failedOnce) {
                dispatch(ToastrAction.error(`${i18n.fetchError}${i18n.AEPoverview}`))
                setResults(prev => ({
                    ...prev,
                    ...fromPairs(ids.map(id => [id, { error: true }])),
                }))
                computeNext(groupNumber + 1, true)
            }
            const resultsById = keyBy(Array.isArray(groupResults) ? groupResults : [], 'id')
            setResults(prev => ({
                ...prev,
                ...fromPairs(ids.map(id => [id, resultsById[id] || { error: true }])),
            }))
            computeNext(groupNumber + 1, failedOnce)
        }
        computeNext(0)
    }, [areProducersLoaded, thresholdRefDefaultSettings, thresholdRefAccountSettings, thresholdRefSettings, thresholdLimDefaultSettings, thresholdLimAccountSettings, thresholdLimSettings, unitsIds])

    const nbReceived = useMemo(() => unitsIds.filter(id => !isNil(results[id])).length, [unitsIds, results])
    const isComputing = autoSearchDone && nbReceived < unitsIds.length
    const progressPercent = unitsIds.length ? nbReceived * 100 / unitsIds.length : 0

    useEffect(() => {
        if (progressPercent >= 100) {
            complianceIsFetched()
        }
    }, [progressPercent])

    const searchStartRef = useRef(moment().valueOf())
    const lastReceptionRef = useRef(moment().valueOf())
    useEffect(() => {
        searchStartRef.current = moment().valueOf()
        lastReceptionRef.current = moment().valueOf()
        setDisplayedProgress(0)
    }, [])
    useEffect(() => {
        lastReceptionRef.current = moment().valueOf()
    }, [nbReceived])
    useEffect(() => {
        const total = unitsIds.length
        if (!isComputing || !total) {
            setDisplayedProgress(progressPercent)
            return () => {}
        }
        const inFlight = Math.min(getGroupSize(total), total - nbReceived)
        const timer = setInterval(() => {
            const now = moment().valueOf()
            const msPerUnit = nbReceived ? (lastReceptionRef.current - searchStartRef.current) / nbReceived : DEFAULT_MS_PER_UNIT
            const extrapolated = Math.min((now - lastReceptionRef.current) / Math.max(msPerUnit, 1), inFlight * MAX_IN_FLIGHT_RATIO)
            setDisplayedProgress(prev => Math.max(prev, (nbReceived + extrapolated) * 100 / total))
        }, PROGRESS_REFRESH_MS)
        return () => clearInterval(timer)
    }, [isComputing, nbReceived, unitsIds.length, progressPercent])

    const dataWithComplianceFilter = useMemo(() => {
        if (!isComplianceFetched) return undefined
        const unitsWithCompliance = data.map(s => {
            const stationCompliance = results[s.id]?.status
            const complianceStatus = getComplianceStatus(stationCompliance)
            const { label, color } = complianceStatus ? complianceStatus : { label: i18n.notDetermined, color: INDICATORS_COLORS.BLACK }
            const renameLabel = label === i18n.noRecentOperation ? i18n.noData : label
            return {
                ...s,
                compliance: stationCompliance,
                complianceRef: linkComplianceValues(stationCompliance),
                conformity: renameLabel,
                conformityChip: { value: <ColourfulChip title={renameLabel} color={color} />, noPadding: true },
            }
        })
        return unitsWithCompliance.filter(s => filters.indicators.includes(s.complianceRef))
    }, [filters.indicators, isComplianceFetched, data])

    useActions(() => accountUser.isAdmin === '1' || accountUser.metadata === '1' ? { new: () => open() } : {}, [data])

    const onValidate = (newElement) => {
        dispatch(DistributionUnitAction.createDistributionUnit(newElement, id => {
            close()
            dispatch(push(`/station/${STATION_TYPE}/${id}/description`))
        }))
    }

    return (
        <Stack spacing={1}>
            <DistributionUnitFilterPanel
                stationType={STATION_TYPE}
                data={distributionUnitsFormatted}
                defaultFilter={filters}
                onValidate={onValidateAll}
                contributorLinks={contributorLinks}
                complianceProgress={displayedProgress}
            />
            <DistributionUnitIndicators
                units={dataWithComplianceFilter || data}
                complianceProgress={displayedProgress}
            />
            <SimpleTabList
                defaultTab={STATION_LIST}
                tabs={[
                    {
                        constant: STATION_LIST,
                        label: i18n.table,
                        icon: 'list',
                    },
                    {
                        constant: MAP,
                        label: i18n.map,
                        icon: 'map',
                    },
                ]}
                noPadding
            >
                {
                    tab => (
                        <>
                            {tab === STATION_LIST && <DashboardStationsPanel
                                stationType={STATION_TYPE}
                                data={{ title: i18n.distributionUnits, stations: dataWithComplianceFilter || data }}
                                headers={distributionDynamicHeaders}
                                exportTableHeaders = {distributionDynamicExportHeaders}
                            />}
                            {tab === MAP && <DistributionUnitsMapLoader
                                stations={dataWithComplianceFilter || data}
                            />}
                        </>
                    )
                }
            </SimpleTabList>
            <DashboardAddStationDialog
                open={isOpened}
                closeDialog={close}
                stations={distributionUnits}
                title={i18n.newDistributionUnit}
                onValidate={onValidate}
            />
        </Stack>
    )
}

DistributionUnitsPanel.propTypes = {
}

const DistributionUnitsDashboard = ({
}) => {
    const {
        cities,
        userBookmarks,
        contributorLinks,
        contributors,
        sandreCodes,
        status,
        distributionUnits,
    } = useSelector(store => ({
        cities: store.CityReducer.cities,
        userBookmarks: store.UserReducer.userBookmarks,
        contributorLinks: store.StationReducer.contributorLinks,
        contributors: store.ContributorReducer.contributors,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        status: store.QualityReducer.status,
        distributionUnits: store.DistributionUnitReducer.distributionUnits,
    }), shallowEqual)

    const dispatch = useDispatch()

    useEffect(() => {
        if (!componentHasHabilitations(H_DISTRIBUTION_DASHBOARD)) {
            dispatch(push('/unauthorized'))
        }
    }, [dispatch])

    const { isLoaded, progress } = useProgressDispatch(() => {
        const promises = !userBookmarks.length ? [UserAction.fetchBookmarks] : []
        const findedStationType = findStationType(STATION_TYPE)
        const promisesContributorsLinks = (!contributorLinks.length && findedStationType.code != 9) ? [...promises, () => StationAction.fetchAllContributors(findedStationType.code)] : promises
        const promisesContributors = !contributors.length ? [...promisesContributorsLinks, ContributorAction.fetchContributors] : promisesContributorsLinks
        const promisesStatus = !status.length ? [...promisesContributors, QualityAction.fetchStatus] : promisesContributors
        const promisesSandreCodes = !sandreCodes.length ? [...promisesStatus, ReferencialAction.fetchSandreCodes] : promisesStatus
        const promisesCities = !cities.length ? [...promisesSandreCodes, CityAction.fetchCities] : promisesSandreCodes
        const promisesDistributionUnit = [
            ...promisesCities,
            () => DistributionUnitAction.fetchDistributionUnitSectors(distributionUnits),
            ManagementUnitAction.fetchManagementUnits,
        ]
        return promisesDistributionUnit.map(p => dispatch(p()))
    }, [])

    useTitle(() => [{
        title: i18n[STATION_TYPE],
        href: STATION_TYPE,
    }, {
        title: i18n.dashboard,
        href: STATION_TYPE,
    }], [STATION_TYPE])

    return (isLoaded) ? (
        <DistributionUnitsPanel />
    ) : (
        <ProgressCard progress={progress} withMessage />
    )
}

DistributionUnitsDashboard.propTypes = {
}

export default DistributionUnitsDashboard
